// Conferência de pessoas (28/09/2026): todo estado parcial à vista — login
// sem ficha, ficha sem login, login bloqueado ou sumido, flag ≠ data, ficha
// inativa sem data, horas sem ficha e pendências do organograma — com o
// botão que resolve cada um. Só o administrador do sistema enxerga.
import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Flex, Space, Tag, Typography, App as AntApp } from 'antd';
import { acaoPessoa, fmtData, NOME_PENDENCIA, type Conferencia } from './apiPessoas';
import ModalNovaPessoa from './ModalNovaPessoa';
import ModalVincularLogin from './ModalVincularLogin';
import ModalReativarPessoa from './ModalReativarPessoa';

const { Text } = Typography;

export default function PainelConferencia({ onAbrirFicha }: { onAbrirFicha?: (profissionalId: string) => void }) {
  const { message, modal } = AntApp.useApp();
  const [c, setC] = useState<Conferencia | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [novaPara, setNovaPara] = useState<{ email: string; nome: string } | null>(null);
  const [vincular, setVincular] = useState<{ id: string; nome: string } | null>(null);
  const [criarLoginPara, setCriarLoginPara] = useState<{ id: string; nome: string } | null>(null);
  const [reativar, setReativar] = useState<{ id: string; nome: string; usuario_email: string | null } | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try { setC(await acaoPessoa<Conferencia>('pessoa_conferencia')); }
    catch (e: any) { message.error('Não foi possível conferir: ' + e.message); }
    finally { setCarregando(false); }
  }, [message]);
  useEffect(() => { carregar(); }, [carregar]);

  async function marcarSemVinculo(email: string) {
    modal.confirm({
      title: `${email} não é uma pessoa da equipe?`,
      content: 'Marca como conta sem vínculo de trabalho (contador, TI, serviço): some daqui e fica fora de horas, cobrança e custo.',
      okText: 'Marcar', cancelText: 'Voltar',
      onOk: async () => {
        try { await acaoPessoa('pessoa_conta_sem_vinculo', { email, marcar: true }); carregar(); }
        catch (e: any) { message.error(e.message); }
      },
    });
  }

  const bloco = (titulo: string, itens: any[], render: (x: any) => any, dica?: string) => itens.length === 0 ? null : (
    <Card size="small" title={<span>{titulo} <Tag>{itens.length}</Tag></span>} style={{ marginBottom: 12 }}>
      {dica && <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>{dica}</Text>}
      <Flex vertical gap={6}>{itens.map(render)}</Flex>
    </Card>
  );
  const linha = (chave: string, texto: any, acoes?: any) => (
    <Flex key={chave} justify="space-between" align="center" gap={8} wrap style={{ fontSize: 13 }}>
      <span>{texto}</span>{acoes && <Space size={6}>{acoes}</Space>}
    </Flex>
  );

  if (carregando && !c) return <div style={{ padding: 30, textAlign: 'center', color: '#888' }}>Conferindo…</div>;
  if (!c) return null;

  return (
    <div>
      <Flex justify="space-between" align="center" style={{ marginBottom: 12 }} wrap gap={8}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          Cada pessoa da equipe precisa ter login e ficha ligados, data de entrada, setor, chefe direto e remuneração vigente. O que falta aparece aqui.
        </Text>
        <Button size="small" onClick={carregar} loading={carregando}>Atualizar</Button>
      </Flex>
      {c.total_pendencias === 0 && <Alert type="success" showIcon message="Tudo em ordem: nenhuma pendência." />}

      {bloco('Logins sem ficha', c.logins_sem_ficha, (l) => linha(l.email,
        <span><strong>{l.nome || l.email}</strong> <Text type="secondary">{l.email}</Text>{!l.enabled && <Tag color="red" style={{ marginLeft: 6 }}>bloqueado</Tag>}</span>,
        <>
          <Button size="small" type="primary" onClick={() => setNovaPara({ email: l.email, nome: l.nome || '' })}>Completar a ficha</Button>
          <Button size="small" onClick={() => marcarSemVinculo(l.email)}>Não é pessoa da equipe</Button>
        </>), 'Entram no sistema, mas as horas que anotam não viram custo e ninguém é cobrado por elas.')}

      {bloco('Fichas ativas sem login', c.fichas_sem_login, (f) => linha(f.profissional_id,
        <span><strong>{f.nome}</strong>{f.setor && <Text type="secondary"> · {f.setor}</Text>}</span>,
        <Button size="small" type="primary" onClick={() => setVincular({ id: f.profissional_id, nome: f.nome })}>Vincular login</Button>),
        'Aparecem na equipe, mas não conseguem anotar horas — e as horas que anotarem com um login solto não chegam à ficha.')}

      {bloco('Horas anotadas sem ficha', c.horas_sem_ficha, (h) => linha(h.usuario_email,
        <span><strong>{h.usuario_email}</strong> — {h.lancamentos} lançamento(s) de {fmtData(h.de)} a {fmtData(h.ate)}</span>),
        'Resolvem-se sozinhas assim que o login for ligado a uma ficha (as horas são religadas na hora).')}

      {bloco('Login bloqueado com ficha ativa', c.login_bloqueado, (f) => linha(f.profissional_id,
        <span><strong>{f.nome}</strong> <Text type="secondary">{f.usuario_email}</Text></span>,
        <Button size="small" onClick={() => setReativar({ id: f.profissional_id, nome: f.nome, usuario_email: f.usuario_email })}>Reativar (libera o login)</Button>),
        'Se a pessoa saiu, use Desligar na ficha; se ficou, reative para liberar o login.')}

      {bloco('Login que não existe mais', c.login_sumiu, (f) => linha(f.profissional_id,
        <span><strong>{f.nome}</strong> <Text type="secondary">{f.usuario_email}</Text></span>,
        <Button size="small" onClick={() => setCriarLoginPara({ id: f.profissional_id, nome: f.nome })}>Criar login</Button>))}

      {bloco('Ativa com data de saída no passado', c.flag_divergente, (f) => linha(f.profissional_id,
        <span><strong>{f.nome}</strong> — saiu em {fmtData(f.desligamento)}</span>),
        'A rotina diária (4h) deixa inativa. Se a data estiver errada, corrija pelo Desligar na ficha.')}

      {bloco('Inativas sem data de saída', c.inativas_sem_data, (f) => linha(f.profissional_id,
        <span><strong>{f.nome}</strong>{f.usuario_email && <Text type="secondary"> · {f.usuario_email}</Text>}</span>,
        onAbrirFicha && <Button size="small" onClick={() => onAbrirFicha(f.profissional_id)}>Abrir ficha</Button>),
        'Fichas antigas arquivadas sem data. Não atrapalham; se quiser, informe a saída pelo Desligar na ficha.')}

      {bloco('Organograma incompleto', c.organograma, (f) => linha(f.profissional_id,
        <span><strong>{f.nome}</strong> — {f.pendencias.map((p: string) => NOME_PENDENCIA[p] || p).join('; ')}</span>,
        onAbrirFicha && <Button size="small" onClick={() => onAbrirFicha(f.profissional_id)}>Abrir ficha</Button>),
        'Sem chefe direto, os alertas de horas vão só para a administração; sem remuneração vigente, as horas não viram custo.')}

      {c.contas_sem_vinculo.length > 0 && (
        <Card size="small" title={<span>Contas sem vínculo de trabalho <Tag>{c.contas_sem_vinculo.length}</Tag></span>}>
          <Flex vertical gap={4}>
            {c.contas_sem_vinculo.map((x) => linha(x.usuario_email,
              <span>{x.usuario_email}{x.motivo && <Text type="secondary"> · {x.motivo}</Text>}</span>,
              <Button size="small" type="link" onClick={async () => { await acaoPessoa('pessoa_conta_sem_vinculo', { email: x.usuario_email, marcar: false }); carregar(); }}>desmarcar</Button>))}
          </Flex>
        </Card>
      )}

      {novaPara && (
        <ModalNovaPessoa aberto emailInicial={novaPara.email} nomeInicial={novaPara.nome}
          onClose={() => setNovaPara(null)} onSalvo={() => carregar()} />
      )}
      {criarLoginPara && (
        <ModalNovaPessoa aberto religar={criarLoginPara}
          onClose={() => setCriarLoginPara(null)} onSalvo={() => carregar()} />
      )}
      {vincular && (
        <ModalVincularLogin aberto ficha={vincular} onClose={() => setVincular(null)}
          onSalvo={(r) => { setVincular(null); message.success(`Login vinculado. ${r.religados ? `${r.religados} hora(s) antiga(s) religadas.` : ''}`); carregar(); }}
          onCriarLogin={() => { setCriarLoginPara(vincular); setVincular(null); }} />
      )}
      {reativar && (
        <ModalReativarPessoa aberto pessoa={reativar} onClose={() => setReativar(null)}
          onSalvo={() => { setReativar(null); message.success('Reativado.'); carregar(); }} />
      )}
    </div>
  );
}
